import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, Typography } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBackOutlined';
import { useHistory, useParams } from 'react-router-dom';
import { SubmitHandler } from 'react-hook-form';
import { useUserById } from 'hooks';
import { MenuProvider } from 'menuContext';
import MultiStepForm from 'components/Forms/MultiStepForm';
import { AppContext } from 'context';
import {
  AdministrationAddSortingCenterUserRoleForm,
  AdministrationSortingCenterUserRoleForPost,
  SortingCenter,
  SortingCenterUserRole,
} from 'types';
import AdministrationLayout from './Layout';
import { Step1, Step2 } from '../../components/Forms/AdministrationSortingCenterUserRolesForm';
import { transformObjectId } from '../../services/apiPlatformTransformer';
import { notify } from '../../components/Notifier';
import { goBack } from '../../utils/history';
import { putAdministrationUserSortingCenterUserRole } from '../../services/apiOperations';

const useStyles = makeStyles({
  headerTitle: {
    marginBottom: '2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'center',
  },
  title: {
    margin: 0,
  },
});

const AdministrationAddSortingCenterUserRole: React.FC = () => {
  const context = useContext(AppContext);
  const history = useHistory();
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'administration.users.sortingCenterUserRole' });
  const sortingCenters = context.getSortingCenters?.();

  const { userId } = useParams<{ userId: string }>();
  const user = useUserById(userId);

  const initialData = {
    userId: userId,
  };

  const eligibleSortingCenter: SortingCenter[] = [];
  sortingCenters?.map((sortingCenterParent: SortingCenter, indexParent) => {
    eligibleSortingCenter.push(sortingCenterParent);
    sortingCenterParent.childrenSortingCenter?.map((sortingCenterChild: SortingCenter, indexChild) => {
      eligibleSortingCenter.push(sortingCenterChild);
      return null;
    });
    return null;
  });

  const onSubmit: SubmitHandler<AdministrationAddSortingCenterUserRoleForm> = async (
    addSortingCenterUserRoleForm: AdministrationAddSortingCenterUserRoleForm,
  ) => {
    const sortingCenterUserRoleInitial: string[] = [];
    user?.sortingCenterUserRoles.map((sortingCenterUserRole: SortingCenterUserRole, index: number) => {
      const sortingCenterId = parseInt(transformObjectId(sortingCenterUserRole.sortingCenter), 10);
      sortingCenterUserRoleInitial[sortingCenterId] = sortingCenterUserRole.role;
      return null;
    });

    const roles: AdministrationSortingCenterUserRoleForPost[] = [];
    addSortingCenterUserRoleForm.sortingCenterUserRole?.map((role: string, sortingCenterId: number) => {
      const sortingCenter = eligibleSortingCenter.find(
        sortingCenter => transformObjectId(sortingCenter) === String(sortingCenterId),
      );
      if (role && sortingCenter && !sortingCenterUserRoleInitial[sortingCenterId]) {
        roles.push({
          role,
          sortingCenter: sortingCenter['@id'],
        });
      }
      return null;
    });

    return putAdministrationUserSortingCenterUserRole(userId, roles)
      .then(() => {
        notify({
          message: t('success'),
          severity: 'success',
        });
        goBack(`/administration/users/${userId}`, history);
      })
      .catch(() => {
        notify({
          message: t('errorGeneric'),
          severity: 'error',
        });
      });
  };

  const steps = [
    { label: t('titleStep1'), component: Step1 },
    { label: t('titleStep2'), component: Step2 },
  ];

  return (
    <>
      <MenuProvider>
        <AdministrationLayout>
          <div className={classes.headerTitle}>
            <Typography variant="h1" className={classes.title}>
              {t('title')}
            </Typography>
          </div>
          <MultiStepForm
            formName="AdministrationAddSortingCenterUserRole"
            backRoute={`/administration/users/${userId}`}
            backIcon={<ArrowBackIcon />}
            onSubmit={onSubmit}
            steps={steps}
            initialData={initialData}
          />
        </AdministrationLayout>
      </MenuProvider>
    </>
  );
};

export default AdministrationAddSortingCenterUserRole;
